import {
  canonicalHuntSnapshotJson,
  type HuntSnapshot,
  parseHuntSnapshot,
} from '@kenocyber/game-domain';
import {
  type ActivePetWire,
  type ArtifactDropWire,
  type ArtifactsResponse,
  type CardDropWire,
  type CardSlot,
  CHECKPOINT_INTERVAL_MS,
  type EconomyMeResponse,
  type EquipmentDropWire,
  effectsFromWire,
  equipmentTotalsFromWire,
  HUNT_SESSION_LEASE_MS,
  type HuntBootstrapResponse,
  type HuntCardActivateRefusal,
  type HuntCardReleaseRefusal,
  type LoadoutWire,
  type MapListResponse,
  type MiningState,
  type OfflineRewardWire,
  ORE_MICRO_PER_UNIT,
  type PetsResponse,
  type PetUnlockResponse,
  type ProgressionWire,
  type SkinHuntReconfiguration,
  sha256Hex,
} from '@kenocyber/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  activateCard,
  activatePet,
  allocatePoints,
  applyPetAppearance,
  assignMiningShip,
  bootstrapHunt,
  cancelMining,
  collectMining,
  combineArtifacts,
  detonateBleed,
  equipItem,
  evolvePet,
  feedPet,
  fetchArtifacts,
  fetchEconomy,
  fetchMapList,
  fetchMiningState,
  fetchPets,
  fetchPlayerState,
  HuntApiError,
  newCommandId,
  newIdempotencyKey,
  releaseCard,
  removeMiningShip,
  restartHunt,
  sendCheckpoint,
  travelToMap,
  unlockPetAppearance,
} from '../api/hunt';
import type { AuthoritativeProgression, GameBridge, HuntSessionMessage } from './bridge';

/**
 * Owns the conversation with the authoritative API.
 *
 * React drives the network; Phaser draws. The loop is: bootstrap once, then a
 * checkpoint every 30 s carrying only identifiers, the simulated delta of that
 * window and a digest of the local state. The server answers with the truth and
 * the scene is reconciled - never the other way round.
 *
 * A checkpoint the server considers early is not a failure: it answers
 * `checkpoint_not_due` with the wait it computed, and the same sequence and
 * idempotency key are repeated when that wait is over.
 *
 * Failure policy: there is NO local economic fallback. If the API is
 * unreachable the same request is retried with the SAME idempotency key,
 * economic progress simply pauses, and the last confirmed state is kept.
 */

/** How often the scene publishes its state, mirrored from `HuntScene`. */
const SNAPSHOT_PERIOD_MS = 250;

export type HuntSyncStatus =
  | 'loading'
  | 'ready'
  | 'syncing'
  | 'saved'
  | 'reconnecting'
  | 'conflict'
  | 'unauthorized'
  /** Between release stages. Terminal: the client stops, it does not retry. */
  | 'maintenance'
  /**
   * Suspended behind an open-world expedition (gate V28I.BZ). Not an error and
   * not terminal: the client asks for nothing - no checkpoint, no bootstrap -
   * until the way back is taken, which is ONE bootstrap.
   */
  | 'suspended'
  | 'failed';

/** Why the hunt is suspended, in the server's own word (gate V28I.BZ). */
export type HuntSuspension = NonNullable<HuntBootstrapResponse['suspendedFor']>;

/**
 * Which door an authoritative read came through. Only the way back from an
 * expedition may end a suspension; every other read is adopted for the scene.
 */
type AuthorityWay = 'read' | 'return';
/** What became of an authoritative read. */
type AuthorityRead = 'adopted' | 'overtaken' | 'failed';

/**
 * Turns a refused journey into something a player can read.
 *
 * The server answers with a stable machine reason, which is the right thing
 * for it to send and the wrong thing to put on a screen. Only reasons travel
 * can actually produce are translated; anything else falls back to the reason
 * itself rather than to a cheerful lie about what went wrong.
 *
 * `map_not_available` deliberately covers three situations - the map does not
 * exist, it is reserved, or it has not been earned - and the wording keeps them
 * indistinguishable, exactly as the server does.
 */
/**
 * Whole ore, for a human.
 *
 * The stored truth is micro-units and stays that way; this is the only place
 * the browser divides, and it divides for display alone.
 */
function formatOre(micro: string): string {
  const value = BigInt(micro);
  const whole = value / ORE_MICRO_PER_UNIT;
  const fraction = ((value % ORE_MICRO_PER_UNIT) * 100n) / ORE_MICRO_PER_UNIT;
  return `${whole.toString(10)},${fraction.toString(10).padStart(2, '0')}`;
}

/**
 * Turns a refused mining action into something a player can read.
 *
 * The server answers with a stable machine reason - right for it to send,
 * wrong to put on a screen. Only reasons mining can actually produce are
 * translated; anything else falls back to the reason itself rather than to a
 * cheerful lie about what went wrong.
 */
function miningRefusal(error: unknown): string {
  const reason = error instanceof HuntApiError ? error.reason : null;
  switch (reason) {
    case 'ship_unavailable':
      return 'Esta nave não está disponível.';
    case 'ship_not_found':
      return 'Nave não encontrada.';
    case 'ship_not_a_ship':
      return 'Isto não é uma nave.';
    case 'fleet_full':
      return 'A frota já está completa.';
    case 'not_assigned':
      return 'Esta nave não está minerando.';
    case 'nothing_to_collect':
      return 'Não há minério para coletar.';
    case 'operation_cancelled':
      return 'A mineração foi cancelada.';
    default:
      return reason ?? 'Não foi possível concluir a ação.';
  }
}

function travelRefusal(error: unknown): string {
  const reason = error instanceof HuntApiError ? error.reason : null;
  if (reason === 'map_not_available') {
    return 'Destino indisponível.';
  }
  if (reason === 'travel_conflict') {
    return 'Outra viagem chegou primeiro.';
  }
  return reason ?? 'Não foi possível viajar.';
}

/**
 * A recusa de uma ordem, como o CLIENTE a pode nomear.
 *
 * As cinco da autoridade, mais uma que só existe deste lado: sem sessão viva
 * não há a quem mandar a ordem, e isso não é uma recusa do servidor - é uma
 * ordem que nunca chegou a ser enviada.
 */
export type CardOrderRefusal =
  | HuntCardActivateRefusal
  /* E as de SOLTAR (gate V28H.N): quase as mesmas, com 'no_charge' no lugar. */
  | HuntCardReleaseRefusal
  | 'no_session';

export interface HuntSessionState {
  readonly status: HuntSyncStatus;
  readonly session: HuntSessionMessage | null;
  readonly sessionId: string | null;
  readonly economic: boolean;
  readonly progression: ProgressionWire | null;
  /** The equipped loadout the SERVER resolved for the current session. */
  readonly loadout: LoadoutWire | null;
  /** Equips an owned item; resolves only once the server confirmed it. */
  readonly equip: (itemInstanceId: string, expectedLoadoutVersion: number) => Promise<void>;
  /**
   * Every artifact and what it is doing, as the SERVER derived it.
   *
   * Null until the first read completes. Refreshed when a drop is confirmed
   * and when a merge succeeds - never patched locally, because which copy is
   * active is the vault's answer, not a guess this hook could make.
   */
  readonly artifacts: ArtifactsResponse | null;
  /**
   * Gold, Cash and VIP, exactly as the server derives them.
   *
   * Null until the first read completes. Re-read after a bootstrap and after a
   * checkpoint that credited something - never on a timer, and never patched
   * locally: the balance the HUD prints beside the predicted Gold is the
   * ledger's answer, and a client-side guess about VIP would be a guess about
   * something the player paid for.
   */
  readonly economy: EconomyMeResponse | null;
  /**
   * Re-reads the wallet and the VIP window from the server.
   *
   * Exposed so a panel that made money move - the store - can bring the HUD up
   * to date without a reload. It sets React state and nothing else: the
   * session, the wave, the boss countdown and the shots in flight are
   * untouched, which is what makes it safe to call while a hunt is running.
   */
  readonly refreshEconomy: () => Promise<void>;
  /**
   * Re-reads the authoritative progression, so the HUD's Gold follows a SPEND.
   *
   * The arena predicts Gold upwards from its own kills and a checkpoint
   * corrects it; nothing told it about money leaving. A panel that charges -
   * the gacha, the marketplace, the store, a trade - calls this so the figure
   * beside the level is the one the server would charge against.
   */
  readonly refreshProgression: () => Promise<void>;
  /** Merges two copies; resolves only once the server confirmed it. */
  readonly combine: (first: string, second: string) => Promise<void>;
  /**
   * Artifacts confirmed by a checkpoint and not yet dismissed.
   *
   * The client never decides a drop: this is populated only by an answer from
   * the server, which is why nothing appears the instant a mob dies.
   */
  readonly drops: readonly ArtifactDropWire[];
  readonly dismissDrop: (dropId: string) => void;
  /** Components and Quantum Stabilisers the server confirmed, in order. */
  readonly equipmentDrops: readonly EquipmentDropWire[];
  /** Cartas que o servidor confirmou. Nunca descartadas em silêncio. */
  readonly cardDrops: readonly CardDropWire[];
  readonly dismissEquipmentDrop: (dropId: string) => void;
  readonly dismissCardDrop: (dropId: string) => void;
  /**
   * Every pet and what it is doing, as the SERVER derived it.
   *
   * Null until the first read completes. Refreshed after an activation and an
   * evolution - never patched locally, because which copy is active is the
   * vault's answer, not a guess this hook could make.
   */
  readonly pets: PetsResponse | null;
  /** The pet the current session is bound to, or null. */
  readonly activePet: ActivePetWire | null;
  /** Activates a pet, or deactivates with `null`. Server-confirmed. */
  readonly setActivePet: (petInstanceId: string | null) => Promise<void>;
  /** Evolves two copies; resolves only once the server confirmed it. */
  readonly evolvePet: (first: string, second: string) => Promise<void>;
  /** Feeds one unit of one food to one pet. Server-confirmed. */
  readonly feedPet: (petInstanceId: string, foodDefinitionId: string) => Promise<void>;
  /**
   * Consumes one unlock item. Refused without spending if already unlocked.
   *
   * Returns the server's OWN palette rather than void. The answer already
   * carries every colour and finish with this account's unlock state and
   * stock, so adopting it is authoritative - there is nothing to invalidate,
   * nothing to re-fetch, and no window in which the two disagree.
   */
  readonly unlockPetAppearance: (unlockDefinitionId: string) => Promise<PetUnlockResponse>;
  /** Wears an already-unlocked appearance. Free, and repeatable. */
  readonly applyPetAppearance: (
    petInstanceId: string,
    energyId: string,
    finishId: string,
  ) => Promise<void>;
  readonly offlineReward: OfflineRewardWire | null;
  readonly dismissOfflineReward: () => void;
  readonly allocate: (allocation: {
    damage: bigint;
    health: bigint;
    speed: bigint;
  }) => Promise<void>;
  /**
   * Manda rebentar as marcas de sangramento desta corrida (gate V28G.N).
   *
   * `no-op` numa sessão de depuração e enquanto uma ordem anterior viaja. Sem
   * marcas, o servidor responde sem escrever nada.
   */
  readonly detonateBleeding: () => Promise<void>;
  /**
   * **Manda ligar a carta deste espaço** (gate V28H.I).
   *
   * A ordem viaja, e o efeito só existe quando a autoridade o confirma: nada é
   * aplicado localmente antes da resposta. Uma activação otimista seria a
   * interface a decidir quando uma habilidade liga.
   *
   * Uma ordem por espaço de cada vez. Um segundo pedido enquanto o primeiro
   * viaja não é enviado - e não é preciso mais do que isso, porque a chave da
   * ordem torna uma repetição que ESCAPE (a rede reenvia, o utilizador insiste
   * noutro separador) inofensiva do lado do servidor.
   *
   * Devolve a recusa nomeada, ou `null` quando a ordem foi aceite.
   */
  readonly activateCardSlot: (slot: CardSlot) => Promise<CardOrderRefusal | null>;
  /** Solta a carga de um espaço (gate V28H.N). O par de cima. */
  readonly releaseCardSlot: (slot: CardSlot) => Promise<CardOrderRefusal | null>;
  readonly restart: () => Promise<void>;
  /**
   * Adopts the run a confirmed skin change reconfigured (gate V23.2A.1).
   *
   * Takes the `hunt` half of a `SkinMutationResponse` the wardrobe already
   * received and the schema already validated - never an instance id, and
   * never a request of its own. `null` means the account was not hunting, and
   * nothing happens: a skin changed in a menu must not start a hunt.
   *
   * Synchronous and total. There is nothing to await and nothing that can
   * fail: the authority arrived with the answer, and this is the client
   * agreeing with it.
   */
  readonly adoptSkinChange: (reconfiguration: SkinHuntReconfiguration | null) => void;
  /**
   * Re-reads the authoritative session and hands it to the scene.
   *
   * The SAME path this hook already takes when a checkpoint comes back as a
   * conflict: the server hands over the one true session and pays any gap as
   * offline farm. Exposed rather than duplicated because a duel needs exactly
   * this on the way out - a hunt that has been away has to be re-authorised,
   * and a second bootstrap would be a second opinion about what the account
   * owns.
   *
   * It resolves once the session has been ADOPTED, which is what lets a caller
   * keep the hunt hidden until there is something correct to show. It restores
   * no snapshot anybody kept locally; there is no such snapshot.
   */
  readonly rebootstrap: () => Promise<void>;
  /**
   * True while the hunt is suspended for an open-world expedition (gate
   * V28I.BZ), whoever said so: the client, the moment it asks to depart, or
   * the server, in a bootstrap answer. Nothing is requested while it holds -
   * not a checkpoint, not a bootstrap, not a restart - and the scene the
   * session feeds is paused and hidden by whoever owns the screen.
   */
  readonly suspended: boolean;
  /**
   * What the AUTHORITY said, in the last bootstrap it answered: the account is
   * on an expedition. Null otherwise - including while the client suspended
   * itself to depart, which is not the server's word yet.
   *
   * The difference is what lets a reload restore the expedition without the
   * way back from one restoring it too.
   */
  readonly suspendedFor: HuntSuspension | null;
  /**
   * Stops the cadence for an expedition. Synchronous and idempotent: every
   * answer still travelling belongs to the hunt being left and is dropped on
   * arrival, and every timer is torn down rather than ignored.
   */
  readonly suspendForOpenWorld: () => void;
  /**
   * The way back: exactly ONE bootstrap, and the cadence restarts only if the
   * server answers with a playable run. If it answers that the account is
   * still out there, the hunt stays suspended - that is the authority
   * speaking. A no-op when nothing is suspended.
   */
  readonly resumeFromOpenWorld: () => Promise<void>;
  /**
   * The maps this account may see, straight from the server.
   *
   * `null` until the screen is opened. Nothing here is derived in the
   * browser: which maps are unlocked is a row in a table only the server can
   * see, and a client that worked the answer out would be a client that could
   * be wrong about it.
   */
  readonly maps: MapListResponse | null;
  readonly mapsLoading: boolean;
  /** True from the moment VIAJAR is confirmed until the answer lands. */
  readonly travelling: boolean;
  /** The server's own words when a journey was refused. Null otherwise. */
  readonly travelError: string | null;
  readonly loadMaps: () => Promise<void>;
  readonly travel: (mapIndex: number) => Promise<void>;
  /**
   * The mining screen, exactly as the server last described it.
   *
   * `null` until the tab is opened. Nothing here is computed in the browser:
   * Power, production and the stored ore are all the server's arithmetic, and
   * a client that recomputed any of them would be a client that could be
   * wrong about what a player owns.
   */
  readonly mining: MiningState | null;
  readonly miningLoading: boolean;
  /** True while an action is in flight. Every button is disabled by it. */
  readonly miningBusy: boolean;
  readonly miningError: string | null;
  /** Set after a successful collection, so the panel can say what it moved. */
  readonly miningNotice: string | null;
  readonly loadMining: () => Promise<void>;
  readonly assignMining: (instanceId: string) => Promise<void>;
  readonly removeMining: (instanceId: string) => Promise<void>;
  readonly collectMining: () => Promise<void>;
  readonly cancelMining: () => Promise<void>;
}

/**
 * Wire shape to bridge shape - a projection, not a conversion.
 *
 * Every field stays a decimal string on the way in: there is no level cap, so
 * narrowing any of these to `number` would silently round the state away.
 */
function toProgressionMessage(wire: ProgressionWire): AuthoritativeProgression {
  return {
    level: wire.level,
    xpIntoLevel: wire.xpIntoLevel,
    lifetimeXp: wire.lifetimeXp,
    gold: wire.gold,
    freePoints: wire.freePoints,
    damagePoints: wire.damagePoints,
    healthPoints: wire.healthPoints,
    attackSpeedPoints: wire.attackSpeedPoints,
  };
}

/**
 * Every answer that authorises a session has the same shape, whether it came
 * from a bootstrap, a restart or a point allocation - so they are adopted by
 * one code path instead of three.
 */
interface AuthorisedState {
  readonly session: HuntBootstrapResponse['session'];
  readonly progression: ProgressionWire;
  readonly loadout: HuntBootstrapResponse['loadout'];
  readonly artifactTotals: HuntBootstrapResponse['artifactTotals'];
  readonly activePet: HuntBootstrapResponse['activePet'];
  readonly snapshot: unknown;
}

function toSessionMessage(response: AuthorisedState): HuntSessionMessage {
  return {
    seed: response.session.seed,
    mapIndex: response.session.mapIndex,
    progression: toProgressionMessage(response.progression),
    // The equipment the SERVER resolved for this session. Passed through
    // untouched: the client renders it and simulates with it, and never
    // decides it.
    loadout: response.loadout,
    // The artifact passives FROZEN into this session, parsed back into exact
    // bigints. Same rule as the loadout: the client simulates with what the
    // server will re-simulate with, and decides none of it.
    artifactEffects: effectsFromWire(response.artifactTotals),
    // And the equipment totals, by exactly the same rule. Their absence was a
    // real defect: without them the arena fought with no components at all
    // while the server credited the real figure.
    equipmentTotals: equipmentTotalsFromWire(response.session.equipmentTotals),
    // The pet the server froze into this session, or null. Same rule again:
    // the scene draws what the server will re-simulate with.
    activePet: response.activePet,
    // Validated before it is trusted, even though it came from our own API.
    snapshot: parseHuntSnapshot(response.snapshot),
  };
}

/**
 * How many drop ids the tab remembers having shown.
 *
 * Large enough that a replay can never outrun it - a lost answer is retried
 * within seconds, not thousands of checkpoints later - and small enough that a
 * session running all day holds a few kilobytes rather than a growing list.
 */
const RECENT_DROP_IDS = 200;

export function useHuntSession(bridge: GameBridge, debug: boolean): HuntSessionState {
  const [status, setStatus] = useState<HuntSyncStatus>('loading');
  const [session, setSession] = useState<HuntSessionMessage | null>(null);
  const [progression, setProgression] = useState<ProgressionWire | null>(null);
  const [loadout, setLoadout] = useState<LoadoutWire | null>(null);
  const [artifacts, setArtifacts] = useState<ArtifactsResponse | null>(null);
  const [economy, setEconomy] = useState<EconomyMeResponse | null>(null);
  const [drops, setDrops] = useState<readonly ArtifactDropWire[]>([]);
  const [equipmentDrops, setEquipmentDrops] = useState<readonly EquipmentDropWire[]>([]);
  const [cardDrops, setCardDrops] = useState<readonly CardDropWire[]>([]);
  /*
   * Every equipment drop id this tab has ALREADY shown.
   *
   * The queue above only holds what is still on screen, so it cannot answer
   * "have we shown this one?" once the toast has been dismissed - and a
   * checkpoint whose answer was lost is retried, and the replay carries the
   * same drops again. Without this the player would see the same component
   * announced twice for one item.
   *
   * A ref rather than state: it must not cause a render, and it must be read
   * synchronously inside the response handler. Bounded, because a long session
   * confirms thousands of checkpoints and an unbounded set is a leak - the
   * oldest ids fall off, which is safe because a replay of a window that old
   * is not something the client will ever be handed.
   *
   * Deliberately NOT localStorage. The vault is the source of truth about what
   * the account owns; this only decides whether a toast has been on screen.
   */
  const shownEquipmentDrops = useRef<string[]>([]);
  const shownCardDrops = useRef<string[]>([]);
  const [pets, setPets] = useState<PetsResponse | null>(null);
  const [activePet, setActivePetState] = useState<ActivePetWire | null>(null);
  const [offlineReward, setOfflineReward] = useState<OfflineRewardWire | null>(null);

  /** Mutable checkpoint bookkeeping, deliberately outside React state. */
  const sessionId = useRef<string | null>(null);
  const economic = useRef(false);
  const nextSequence = useRef(1);
  /** Simulated time of the local run, and where the current window opened. */
  const localTickMs = useRef(0);
  const windowStartTickMs = useRef(0);
  /** The client's own deterministic state at `localTickMs`, for the digest. */
  const localSnapshot = useRef<HuntSnapshot | null>(null);
  /** Pending "not due" retry, so an early window is repeated exactly once. */
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Reused across retries so a lost response can never double-credit. */
  const pendingKey = useRef<string | null>(null);
  const inFlight = useRef(false);
  /**
   * The server shut the game. Nothing is attempted again, by anything.
   *
   * A terminal latch rather than a status, because status flips back and forth
   * with the ordinary sync and this must not: a refused checkpoint used to fall
   * into "reconnecting" and be retried every thirty seconds for as long as the
   * tab stayed open, which is a client hammering a server that is deliberately
   * refusing it, forever. Once this is set the checkpoint interval is torn down
   * and every write returns without asking.
   *
   * It is only ever cleared by the whole tree being rebuilt, which is what the
   * availability gate does when the service comes back.
   */
  const halted = useRef(false);
  /**
   * The expedition the hunt is suspended for, or null (gate V28I.BZ).
   *
   * A ref for the same reason `halted` is one: the cadence, the retry timer and
   * every write read it synchronously, and a status flips back and forth with
   * ordinary sync. The two states beside it are what React renders.
   *
   * Only two things end it: the way back (`resumeFromOpenWorld`) and the server
   * answering the way back with a playable run. A playable answer that arrives
   * by any other door - a first bootstrap still in flight when the pilot left,
   * a restart that was already travelling - is adopted for the scene and
   * leaves the hunt suspended, because the pilot is somewhere else.
   */
  const suspension = useRef<HuntSuspension | null>(null);
  const [suspended, setSuspended] = useState(false);
  const [suspendedFor, setSuspendedFor] = useState<HuntSuspension | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const runCheckpointRef = useRef<(() => Promise<void>) | null>(null);
  /** The one and only first bootstrap, shared across effect re-runs. */
  const firstBootstrap = useRef<Promise<HuntBootstrapResponse> | null>(null);
  /** Guards against a second click while a spend is still in flight. */
  const allocating = useRef(false);
  /** O mesmo guarda, para a ordem de detonar (gate V28G.N). */
  const detonating = useRef(false);
  /**
   * Que espaços têm uma ordem em VOO, para o segundo clique não repetir.
   *
   * Por espaço e não global: os três arrefecem sozinhos, e uma ordem no espaço
   * 1 não tem nada que impedir uma no espaço 2.
   */
  const cardOrdersInFlight = useRef(new Set<CardSlot>());
  const cardReleasesInFlight = useRef(new Set<CardSlot>());
  /** Reused across retries so a lost answer never spends the points twice. */
  const allocationKey = useRef<string | null>(null);
  /**
   * Bumped every time a new session is adopted.
   *
   * A checkpoint can easily still be in flight - or waiting out a "not due"
   * retry - when points are spent and the server hands back a replacement
   * session. Its answer belongs to a session that no longer exists: applying
   * its progression, its correction or its error to the new one would
   * reconcile the arena to a run nobody is playing. Every request captures the
   * generation it was issued under and its result is dropped unless the
   * generation still matches.
   */
  const generation = useRef(0);

  /**
   * The order authoritative READS were asked for, so the newest INTENT wins.
   *
   * ## The soft-lock this exists for (gate V27A.1)
   *
   * The client asks for an authoritative state through more than one door and
   * they overlap. It re-bootstraps on a checkpoint conflict, and again when the
   * PvP feature re-authorises the hunt on the way home from a match; meanwhile
   * the player can press the death screen's button, which restarts the run. A
   * bootstrap that was already in flight when that press happened describes the
   * run that just died - and it was being adopted on arrival, putting the corpse
   * back with the button apparently doing nothing. Measured in the 3x3 soak:
   * `restart:200/active` twice, then `bootstrap:200/dead`, twelve times in three
   * minutes with the server answering correctly every time.
   *
   * ## Why arrival order is the wrong rule, in BOTH directions
   *
   * "Adopt whatever lands last" loses to the stale read above. "Drop anything
   * whose session changed while it was away" - the first fix tried here - loses
   * the other way: the bootstrap adopts first, and the RESTART, which the player
   * asked for later, is then thrown away as stale and the death screen stays up.
   * That was measured too, in cycle 5 of the same soak.
   *
   * So each request takes a ticket when it is ISSUED, and an answer is adopted
   * only if no LATER-issued answer has been adopted already. Whichever request
   * was made last wins, however the network reorders them.
   */
  const issued = useRef(0);
  const lastAdopted = useRef(0);

  /**
   * How many restarts are in flight, because a restart is a WRITE.
   *
   * Ordering reads by issue is not enough on its own, and the soak said so: the
   * player presses the button, a checkpoint belonging to the run that just ended
   * answers 409, and the conflict handler re-bootstraps. That read is issued
   * LATER than the press, so it wins on tickets - and it asks the server about a
   * session the restart has not finished replacing, so it comes back `dead` and
   * puts the death screen straight back up. Eleven presses, every request
   * answered correctly, the ship dead throughout.
   *
   * Nothing read about a session can be newer than the write that is replacing
   * it, whenever it was asked for. So a read that overlaps a restart is dropped
   * on arrival: the restart's own answer is the authoritative state, and the
   * next read after it will describe the run it created.
   */
  const restartsInFlight = useRef(0);

  const [mining, setMining] = useState<MiningState | null>(null);
  const [miningLoading, setMiningLoading] = useState(false);
  const [miningBusy, setMiningBusy] = useState(false);
  const [miningError, setMiningError] = useState<string | null>(null);
  const [miningNotice, setMiningNotice] = useState<string | null>(null);
  /**
   * The action in flight, if any.
   *
   * A ref rather than the state beside it, because two clicks in the same
   * frame would both read the same stale boolean. The server serialises them
   * anyway; a request that was never sent cannot produce a second anything.
   */
  const miningInFlight = useRef(false);
  /**
   * The collection's replay key, kept for as long as ONE intention lasts.
   *
   * A retry of the same click reuses it, so a lost answer costs the player
   * nothing. A new click mints a new one, because that is a different
   * intention and must be able to collect again.
   */
  const miningCollectKey = useRef<string | null>(null);

  const [maps, setMaps] = useState<MapListResponse | null>(null);
  const [mapsLoading, setMapsLoading] = useState(false);
  const [travelling, setTravelling] = useState(false);
  const [travelError, setTravelError] = useState<string | null>(null);
  /**
   * The journey in flight, if any.
   *
   * A ref rather than the state above, because two clicks in the same frame
   * would both read the same stale `travelling`. The server serialises them
   * anyway - it locks the progression row - but a request that was never sent
   * cannot produce a second session, a second toast or a second anything.
   */
  const travelInFlight = useRef(false);

  /**
   * Takes ownership of a newly authorised session.
   *
   * Everything belonging to the previous one is dropped here, in one place: a
   * pending retry timer, the idempotency key of a window that will never be
   * credited now, and the local tick. The generation moves last, so anything
   * still in flight fails its check on the way back.
   */
  /**
   * Retires everything that belonged to the previous authoritative state.
   *
   * A pending retry timer, the idempotency key of a window that will never be
   * credited now, and the generation an in-flight checkpoint captured. Called
   * both when a new session is adopted and when points are spent: an
   * allocation consumes a sequence too, so a checkpoint already travelling is
   * just as stale as it would be after a session swap.
   */
  const invalidateInFlight = useCallback(() => {
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    pendingKey.current = null;
    generation.current += 1;
  }, []);

  const adopt = useCallback(
    (response: AuthorisedState & { offlineReward?: unknown }) => {
      invalidateInFlight();
      sessionId.current = response.session.sessionId;
      economic.current = response.session.economic;
      nextSequence.current = response.session.lastSequence + 1;
      localTickMs.current = response.session.acceptedTickMs;
      windowStartTickMs.current = response.session.acceptedTickMs;
      localSnapshot.current = null;
      const message = toSessionMessage(response);
      setSession(message);
      setProgression(response.progression);
      setLoadout(response.loadout);
      setActivePetState(response.activePet);
      setStatus('ready');
      return message;
    },
    [invalidateInFlight],
  );

  const applyBootstrap = useCallback(
    (response: HuntBootstrapResponse, way: 'read' | 'return' = 'read') => {
      const message = adopt(response);
      /*
       * The authority's word on whether this run may be played (gate V28I.BZ),
       * applied where the run is adopted so no timer ever sees one without the
       * other. A suspended answer stops the cadence whoever asked; a playable
       * one ends a suspension only when it answers the way back.
       */
      if (response.suspendedFor !== undefined) {
        suspension.current = response.suspendedFor;
        setSuspended(true);
        setSuspendedFor(response.suspendedFor);
      } else if (way === 'return' || suspension.current === null) {
        suspension.current = null;
        setSuspended(false);
        setSuspendedFor(null);
      }
      if (suspension.current !== null) {
        setStatus('suspended');
      }
      if (response.offlineReward) {
        setOfflineReward(response.offlineReward);
      }
      return message;
    },
    [adopt],
  );

  /**
   * Re-reads the artifact state from the server.
   *
   * Called after a bootstrap, after a confirmed drop and after a merge - never
   * on a timer. There is nothing to poll for: a drop arrives inside a
   * checkpoint answer, and a merge is a request this hook made itself.
   */
  const refreshPets = useCallback(async () => {
    try {
      setPets(await fetchPets());
    } catch {
      // A failed read leaves the previous state on screen: it describes pets
      // the server already owns, and a stale copy beats an empty one.
    }
  }, []);

  const refreshArtifacts = useCallback(async () => {
    try {
      setArtifacts(await fetchArtifacts());
    } catch {
      // A failed read leaves the previous state on screen. It is a display of
      // passives the server already applied; a stale copy is far better than
      // an empty one, and the next refresh corrects it.
    }
  }, []);

  const refreshEconomy = useCallback(async () => {
    try {
      setEconomy(await fetchEconomy());
    } catch {
      // A failed read leaves the previous state on screen. It describes money
      // the server already holds; a stale copy beats an empty one, and the
      // next refresh corrects it.
    }
  }, []);

  /**
   * Re-reads the authoritative progression and hands it to the arena.
   *
   * ## Why a spend needs this and a kill does not
   *
   * The HUD's Gold is the SIMULATION's figure. The arena predicts it from the
   * kills it just drew, and every checkpoint replaces that prediction with the
   * server's own - which is exactly right while Gold only ever goes UP.
   *
   * It fails the moment a panel spends it. The gacha charged, its own panel
   * showed the new balance because it re-read the banner, and the HUD went on
   * advertising the old one: two numbers for the same money, on the same
   * screen, and the bigger one wrong. A player deciding whether to roll again
   * was reading a balance that no longer existed - and on a run that dies
   * often, the correction arrived with the death rather than with the spend,
   * because a restart bootstraps and a bootstrap re-reads.
   *
   * ## What it does, and what it deliberately does not
   *
   * The same reconciliation a checkpoint performs, through the same message,
   * so nothing visual jumps: entities, timers, accumulators and the RNG are
   * untouched, and no reward comes from it.
   *
   * It does NOT credit the window in flight - it is a read, not a checkpoint -
   * so the figure it adopts can be a few seconds of kills behind the
   * prediction it replaces. That is the right way round. This is the money the
   * server actually holds, it is the number every panel charges against, and
   * the next checkpoint hands the difference back. Showing more Gold than
   * exists is what sent a player to an empty wallet; showing a moment less
   * costs nothing.
   */
  const refreshProgression = useCallback(async () => {
    try {
      const state = await fetchPlayerState();
      setProgression(state.progression);
      bridge.sendToGame('hunt:reconcile', {
        progression: toProgressionMessage(state.progression),
      });
    } catch {
      // Same rule as the economy read above: the previous figure stays, and
      // the next checkpoint corrects it anyway.
    }
  }, [bridge]);

  const readAuthority = useCallback(
    async (way: AuthorityWay): Promise<AuthorityRead> => {
      /*
       * The session this read belongs to (gate V27A.1).
       *
       * A bootstrap is not always the newest thing the client wants. It is fired
       * on a checkpoint conflict, and again when the PvP feature re-authorises
       * the hunt on the way home from a match - and while it is away the player
       * can press the death screen's button, which opens a NEW run. The answer to
       * this read then describes the run that just died, and adopting it puts the
       * corpse back on screen with the button apparently doing nothing.
       *
       * Measured exactly that way in the 3x3 soak: `restart:200/active` twice,
       * then `bootstrap:200/dead`, then the death screen again, twelve times in
       * three minutes with every request answered correctly by the server.
       *
       * The checkpoint path has always carried this rule. These are the other two
       * doors an authoritative state comes through, and they need it for the same
       * reason: an answer that belongs to a replaced session is not a correction,
       * it is a ghost.
       */
      const ticket = ++issued.current;
      const overlappedRestart = () => restartsInFlight.current > 0;
      try {
        const response = await bootstrapHunt({ debug });
        if (ticket < lastAdopted.current || overlappedRestart()) {
          return 'overtaken';
        }
        lastAdopted.current = ticket;
        const message = applyBootstrap(response, way);
        // A session already on screen is swapped in place; the first one is
        // handed to the scene through props instead, so nothing is recreated.
        bridge.sendToGame('hunt:session', message);
        void refreshArtifacts();
        void refreshPets();
        void refreshEconomy();
        return 'adopted';
      } catch (error) {
        /* A failure that belongs to a superseded read is a ghost too: reporting
         * it would put a live client into `reconnecting` over an answer nobody is
         * waiting for any more - and the same goes for a read a restart overtook. */
        if (ticket < lastAdopted.current || overlappedRestart()) {
          return 'overtaken';
        }
        if (error instanceof HuntApiError && error.isUnauthorized) {
          setStatus('unauthorized');
          return 'failed';
        }
        setStatus((current) => (current === 'loading' ? 'failed' : 'reconnecting'));
        return 'failed';
      }
    },
    [applyBootstrap, bridge, debug, refreshArtifacts, refreshPets, refreshEconomy],
  );

  /** The ordinary re-read: a checkpoint conflict, a duel's way home. */
  const runBootstrap = useCallback(async () => {
    await readAuthority('read');
  }, [readAuthority]);

  /**
   * Stops the hunt for an expedition (gate V28I.BZ, ADR 0128).
   *
   * Called the moment the pilot asks to depart, before the server has said
   * anything: the entry is what ends the session there, and a checkpoint that
   * raced it used to come back 409 and re-bootstrap a fresh run behind the
   * expedition - every half minute, for as long as the expedition lasted.
   *
   * Everything still travelling belongs to the hunt being left. A checkpoint's
   * answer - or its 409 - fails its generation check and is dropped, the retry
   * timer goes, and the cadence effect tears its interval down on this render
   * rather than leaving it firing into a guard.
   */
  const suspendForOpenWorld = useCallback(() => {
    suspension.current = 'open_world';
    invalidateInFlight();
    setSuspended(true);
    setStatus('suspended');
  }, [invalidateInFlight]);

  const resumeFromOpenWorld = useCallback(async () => {
    if (suspension.current === null) {
      return;
    }
    if ((await readAuthority('return')) === 'failed' && suspension.current !== null) {
      /*
       * The way back could not be read. The ordinary recovery takes over from
       * here: the cadence restarts, its first answer is a conflict about the
       * run the expedition closed, and that re-bootstraps - one read per
       * window, the rule every other outage already follows.
       */
      suspension.current = null;
      setSuspended(false);
      setSuspendedFor(null);
    }
  }, [readAuthority]);

  // First bootstrap. The arena is not rendered until this resolves.
  //
  // Exactly ONE request, however many times the effect runs. React's
  // development double-mount would otherwise send two, and the second would
  // settle a few milliseconds of tail and move the session's sequence forward -
  // leaving whichever answer the component kept describing a state that is
  // already one step behind, so the very first checkpoint came back as a
  // conflict. The promise is shared instead, and the surviving run applies it.
  useEffect(() => {
    let active = true;
    /* The first read takes a ticket like every other one: it is the earliest
     * intent there is, so anything the player asks for while it is in flight
     * has to win over it. */
    const ticket = ++issued.current;
    firstBootstrap.current ??= bootstrapHunt({ debug });
    firstBootstrap.current
      .then((response) => {
        if (active && ticket >= lastAdopted.current) {
          lastAdopted.current = ticket;
          applyBootstrap(response);
          void refreshArtifacts();
          void refreshPets();
          void refreshEconomy();
        }
      })
      .catch((error: unknown) => {
        if (!active) {
          return;
        }
        // A failed first bootstrap must be retryable, not cached forever.
        firstBootstrap.current = null;
        /*
         * Maintenance is its own terminal state, and it must not retry.
         *
         * The arena is never mounted, so nothing simulates behind the notice -
         * which is the point: a client that kept ticking would be minting
         * progress against a wallet the reset is rewriting.
         */
        if (error instanceof HuntApiError && error.isServiceClosed) {
          halted.current = true;
        }
        setStatus(
          error instanceof HuntApiError && error.isServiceClosed
            ? 'maintenance'
            : error instanceof HuntApiError && error.isUnauthorized
              ? 'unauthorized'
              : 'failed',
        );
      });
    return () => {
      active = false;
    };
  }, [applyBootstrap, debug, refreshArtifacts, refreshPets, refreshEconomy]);

  // The local tick and the state that belongs to it. Reported as a bound and
  // a digest respectively - never as a reward.
  useEffect(
    () =>
      bridge.onUiMessage('hunt:snapshot', (message) => {
        localTickMs.current = message.tickMs;
        localSnapshot.current = message.deterministicSnapshot;
      }),
    [bridge],
  );

  const markSaved = useCallback(() => {
    setStatus('saved');
    if (savedTimer.current) {
      clearTimeout(savedTimer.current);
    }
    savedTimer.current = setTimeout(() => {
      setStatus((current) => (current === 'saved' ? 'ready' : current));
    }, 2_000);
  }, []);

  const runCheckpoint = useCallback(async () => {
    const id = sessionId.current;
    // Debug sessions are non-economic: the client does not even ask, and the
    // server would refuse if it did. A halted client asks for nothing at all,
    // and neither does one suspended behind an expedition (gate V28I.BZ).
    if (
      halted.current ||
      suspension.current !== null ||
      !id ||
      !economic.current ||
      inFlight.current
    ) {
      return;
    }
    inFlight.current = true;
    setStatus('syncing');
    // Same key on every retry of THIS window.
    pendingKey.current ??= newIdempotencyKey('cp');
    // The session this request belongs to. If points are spent while it is in
    // flight, the answer below describes a session that no longer exists.
    const issuedFor = generation.current;
    const stale = () => generation.current !== issuedFor;
    try {
      const local = localSnapshot.current;
      const response = await sendCheckpoint({
        sessionId: id,
        sequence: nextSequence.current,
        // A DELTA for this window, never an absolute tick: a session that has
        // been running for days is still a normal 30 s window, and the lease
        // is the only ceiling the contract needs.
        clientDeltaMs: Math.min(
          Math.max(0, localTickMs.current - windowStartTickMs.current),
          HUNT_SESSION_LEASE_MS,
        ),
        // Same canonical serialisation the server uses. Detection only.
        ...(local === null
          ? {}
          : { clientDigest: await sha256Hex(canonicalHuntSnapshotJson(local)) }),
        idempotencyKey: pendingKey.current,
      });
      if (stale()) {
        // A newer session was adopted while this was travelling. Its
        // progression, its tick and its correction all belong to a run nobody
        // is playing: applying any of them would reconcile the arena backwards.
        // Nothing is lost - the server settled that session's tail when it
        // handed over the new one.
        return;
      }
      nextSequence.current = response.session.lastSequence + 1;
      windowStartTickMs.current = response.session.acceptedTickMs;
      pendingKey.current = null;
      setProgression(response.progression);
      bridge.sendToGame('hunt:reconcile', {
        progression: toProgressionMessage(response.progression),
      });
      if (response.correction) {
        // A real divergence - the browser lost frames, or the server capped
        // the window. Continue from the authoritative run, validated before it
        // is trusted. Nothing visual is recreated and no reward comes from it.
        bridge.sendToGame('hunt:correct', {
          snapshot: parseHuntSnapshot(response.correction.authoritativeSnapshot),
        });
        localTickMs.current = response.correction.authoritativeTickMs;
      }
      if (response.artifacts) {
        // A drop landed. The new copy applies FROM THIS BOUNDARY: the server
        // has already reconfigured its own simulation, and this hands the same
        // numbers to ours - no snapshot, because nothing about the arena moves.
        // Same wave, same boss, same shots in the air with the damage they were
        // born with; only later volleys use the new attributes.
        setArtifacts(response.artifacts);
        bridge.sendToGame('hunt:artifacts', {
          artifactEffects: effectsFromWire(response.artifacts.totals),
        });
      }
      if (response.equipmentDrops.length > 0) {
        /*
         * Deduplicated by the SERVER's drop id, never by name, rarity or
         * Power: two genuinely different components of the same type and
         * rarity are indistinguishable by those, and suppressing the second
         * would hide an item the player really owns.
         */
        const fresh = response.equipmentDrops.filter(
          (drop) => !shownEquipmentDrops.current.includes(drop.dropId),
        );
        if (fresh.length > 0) {
          shownEquipmentDrops.current = [
            ...shownEquipmentDrops.current,
            ...fresh.map((drop) => drop.dropId),
          ].slice(-RECENT_DROP_IDS);
          setEquipmentDrops((current) => [...current, ...fresh]);
        }
      }
      if (response.drops.length > 0) {
        // Announced only now, because only now is it true. The client has no
        // loot seed and cannot roll: it has nothing to announce until the
        // server says an artifact fell.
        setDrops((current) => [...current, ...response.drops]);
      }
      if (response.cardDrops.length > 0) {
        /*
         * A terceira loteria, anunciada pela mesma regra (gate V28H.E).
         *
         * Deduplicada pelo id de queda do SERVIDOR e nunca pelo nome: duas
         * cópias da mesma carta são dois itens, e suprimir a segunda esconderia
         * uma que o jogador realmente tem.
         */
        const freshCards = response.cardDrops.filter(
          (drop) => !shownCardDrops.current.includes(drop.dropId),
        );
        if (freshCards.length > 0) {
          shownCardDrops.current = [
            ...shownCardDrops.current,
            ...freshCards.map((drop) => drop.dropId),
          ].slice(-RECENT_DROP_IDS);
          setCardDrops((current) => [...current, ...freshCards]);
        }
      }
      // Re-read the wallets after a checkpoint that credited: the Gold the HUD
      // predicts is the simulation's, and this is the ledger's. VIP travels
      // with it, so an entitlement that expired mid-window stops being
      // advertised on the very next boundary rather than at the next reload.
      if (response.credited.goldEarned !== '0' || response.credited.xpEarned !== '0') {
        void refreshEconomy();
      }
      markSaved();
    } catch (error) {
      if (stale()) {
        // The failure belongs to the previous session too - including a 409
        // that only means "you already moved on". Never re-bootstrap on it.
        return;
      }
      if (error instanceof HuntApiError && error.isNotDue) {
        // Not a failure and not a conflict: the window simply is not over.
        // Wait exactly what the server computed, keep the sequence and the
        // key, and say nothing alarming to the player.
        setStatus((current) => (current === 'syncing' ? 'ready' : current));
        if (retryTimer.current) {
          clearTimeout(retryTimer.current);
        }
        retryTimer.current = setTimeout(
          () => {
            void runCheckpointRef.current?.();
          },
          // One snapshot period of margin: the reported delta is as old as the
          // last push, so retrying at the bare minimum would be early again.
          (error.retryAfterMs ?? CHECKPOINT_INTERVAL_MS) + SNAPSHOT_PERIOD_MS,
        );
      } else if (error instanceof HuntApiError && error.isServiceClosed) {
        /*
         * The game shut WHILE this player was in it.
         *
         * Terminal, and it stops everything: no retry, no re-bootstrap, no
         * further checkpoint. The refusal has already been reported to the
         * availability gate by the API client, so the whole tree is about to be
         * replaced by the right screen - but the latch is what guarantees this
         * hook asks for nothing in the meantime, and it is set here rather than
         * inferred from a status that flips back and forth with ordinary sync.
         *
         * Anything the local run simulated past the last accepted checkpoint is
         * discarded rather than sent. It was never credited, the server does not
         * know about it, and a reset may be rewriting the wallet it would have
         * been credited to.
         */
        halted.current = true;
        pendingKey.current = null;
        if (retryTimer.current) {
          clearTimeout(retryTimer.current);
          retryTimer.current = null;
        }
        setStatus('maintenance');
      } else if (error instanceof HuntApiError && error.isUnauthorized) {
        setStatus('unauthorized');
      } else if (error instanceof HuntApiError && error.isConflict) {
        // Another tab, a stale sequence or an expired lease. Re-bootstrap:
        // the server hands back the one true session and pays any gap as
        // offline farm.
        setStatus('conflict');
        pendingKey.current = null;
        await runBootstrap();
      } else {
        // Network or 5xx: keep the key, keep the last confirmed progress, and
        // try again on the next tick. Never credit anything locally.
        setStatus('reconnecting');
      }
    } finally {
      inFlight.current = false;
    }
  }, [bridge, markSaved, runBootstrap, refreshEconomy]);

  // Reachable from the "not due" retry without making the callback depend on
  // itself. There is only ever one checkpoint in flight.
  useEffect(() => {
    runCheckpointRef.current = runCheckpoint;
  }, [runCheckpoint]);

  // The 30 s cadence. Never per shot, per hit or per frame - and not at all
  // once the server has shut the game, nor while the hunt is suspended behind
  // an expedition (gate V28I.BZ): the timer is torn down rather than left
  // firing requests that are being refused on purpose.
  useEffect(() => {
    if (!session || status === 'maintenance' || suspended) {
      return;
    }
    const interval = setInterval(() => {
      void runCheckpoint();
    }, CHECKPOINT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [runCheckpoint, session, status, suspended]);

  useEffect(
    () => () => {
      if (savedTimer.current) {
        clearTimeout(savedTimer.current);
      }
      if (retryTimer.current) {
        clearTimeout(retryTimer.current);
      }
    },
    [],
  );

  const allocate = useCallback(
    async (allocation: { damage: bigint; health: bigint; speed: bigint }) => {
      if (!economic.current) {
        // A debug session never checkpointed, so the server holds no points to
        // spend. The panel stays usable for testing and persists nothing.
        bridge.sendToGame('hunt:dev-allocate', allocation);
        return;
      }
      // One allocation at a time. A second click while the first is still
      // travelling would spend points the server has not answered for yet.
      // A halted client spends nothing at all: the server is refusing writes on
      // purpose, and the arena it would reconfigure is already gone.
      if (allocating.current || halted.current) {
        return;
      }
      allocating.current = true;
      // The SAME key for every retry of this spend: a lost answer must never
      // become a second allocation.
      allocationKey.current ??= newIdempotencyKey('alloc');
      setStatus('syncing');
      try {
        const response = await allocatePoints({
          // Decimal strings: the only ceiling is the balance actually owned.
          damage: allocation.damage.toString(),
          health: allocation.health.toString(),
          attackSpeed: allocation.speed.toString(),
          idempotencyKey: allocationKey.current,
        });
        allocationKey.current = null;
        // The settlement inside the allocation consumed a sequence, so any
        // checkpoint still travelling belongs to a state that has moved on.
        invalidateInFlight();
        // The SAME session continues - the server applied the points to the
        // hunt in flight. Adopting a "new" session here (or bootstrapping
        // again) is what used to wipe the wave: the scene cleared its mobs,
        // boss and shots and started over for what should be a change of
        // numbers. Only the progression and the reconciled snapshot travel.
        sessionId.current = response.session.sessionId;
        nextSequence.current = response.session.lastSequence + 1;
        windowStartTickMs.current = response.session.acceptedTickMs;
        setProgression(response.progression);
        /*
         * Unconditional, and safe: this line is unreachable outside an
         * economic session. A non-economic one returned above, over
         * `hunt:dev-allocate`, because the server holds no points to spend.
         * The two paths below - equipping and merging - have no such early
         * return and do have to ask.
         */
        bridge.sendToGame('hunt:reconfigure', {
          snapshot: parseHuntSnapshot(response.snapshot),
        });
        markSaved();
      } catch (error) {
        // The point is NOT applied locally: the arena keeps playing with the
        // attributes it had, the key is kept so the retry is the same spend,
        // and the player is told discreetly.
        setStatus(
          error instanceof HuntApiError && error.isUnauthorized ? 'unauthorized' : 'reconnecting',
        );
      } finally {
        allocating.current = false;
      }
    },
    [bridge, invalidateInFlight, markSaved],
  );

  /**
   * Equips an owned item and keeps the run going.
   *
   * Nothing is applied optimistically: the local state moves only after the
   * server confirmed the swap, because the vault - not this hook - decides
   * what is equipped. On failure the previous loadout simply stays, the arena
   * keeps playing, and the caller shows a short message.
   *
   * Like an allocation, the answer reconfigures the SAME session in place. The
   * arena is never recreated, so mobs, boss, timers and shots in flight all
   * continue exactly as they were.
   */
  /**
   * **Manda rebentar as marcas de sangramento desta corrida** (gate V28G.N).
   *
   * O MESMO caminho que gastar um ponto segue, e é isso que o torna legítimo: a
   * caça não tem canal de input por tique, e uma detonação aplicada só aqui
   * produziria uma instantânea que a re-simulação do servidor nunca alcança - o
   * digest seguinte seria uma correção, com as marcas de volta e o dano por
   * acontecer. O servidor liquida a cauda, executa a ordem e devolve a MESMA
   * sessão reconfigurada.
   *
   * Nada é aplicado localmente antes da resposta. Uma detonação otimista seria a
   * cena a decidir dano.
   *
   * Uma de cada vez: um segundo clique enquanto a primeira viaja pediria uma
   * ordem sobre um estado que o servidor já mudou. Não há chave de idempotência,
   * e a razão está no cliente da API - uma repetição encontra zero marcas.
   *
   * Numa sessão de DEPURAÇÃO não faz nada: ela não é económica, a rota recusa-a,
   * e uma instantânea não liquidada rebobinaria a arena se fosse adotada.
   */
  const detonateBleeding = useCallback(async () => {
    const id = sessionId.current;
    if (!economic.current || id === null) {
      return;
    }
    if (detonating.current || halted.current) {
      return;
    }
    detonating.current = true;
    setStatus('syncing');
    try {
      const response = await detonateBleed(id);
      /*
       * A liquidação dentro da ordem consumiu uma sequência, portanto um
       * checkpoint ainda em voo pertence a um estado que já andou.
       */
      invalidateInFlight();
      sessionId.current = response.session.sessionId;
      nextSequence.current = response.session.lastSequence + 1;
      windowStartTickMs.current = response.session.acceptedTickMs;
      setProgression(response.progression);
      /*
       * A instantânea autoritativa e o que rebentou, na mesma mensagem.
       *
       * A cena precisa das duas ao mesmo tempo: o efeito tem de ser desenhado
       * onde os alvos ESTAVAM, e a adoção substitui a simulação - incluindo um
       * alvo que a detonação tenha matado.
       */
      bridge.sendToGame('hunt:bleed-detonated', {
        snapshot: parseHuntSnapshot(response.snapshot),
        detonated: response.detonated,
      });
      markSaved();
    } catch (error) {
      /*
       * Nada rebentou. As marcas continuam onde estavam - elas vivem no
       * servidor - e o jogador pode voltar a carregar.
       */
      setStatus(
        error instanceof HuntApiError && error.isUnauthorized ? 'unauthorized' : 'reconnecting',
      );
    } finally {
      detonating.current = false;
    }
  }, [bridge, invalidateInFlight, markSaved]);

  /**
   * **A ORDEM de ligar uma carta, e a única fronteira por onde ela passa.**
   *
   * O rato e o teclado chamam esta função e mais nenhuma: um caminho, uma
   * validação, uma recusa. Dois caminhos seriam duas oportunidades de divergir
   * sobre o que "disponível" quer dizer.
   *
   * ## Nada acontece antes da confirmação
   *
   * Nem efeito, nem duração, nem arrefecimento. A barra continua a ler a
   * instantânea, e a instantânea só muda quando a autoridade responde - o que é
   * também o que faz uma RECUSA corrigir a barra em vez de a deixar mentir.
   *
   * ## Uma resposta atrasada não regride nada
   *
   * A resposta é descartada quando a sessão a que pertence já não é a activa. É
   * a mesma regra do checkpoint, e é ela que impede uma ordem em voo durante uma
   * reconfiguração de reinstalar uma instantânea antiga.
   */
  const activateCardSlot = useCallback(
    async (slot: CardSlot): Promise<CardOrderRefusal | null> => {
      const id = sessionId.current;
      if (!economic.current || id === null || halted.current) {
        return 'no_session';
      }
      if (cardOrdersInFlight.current.has(slot)) {
        return null;
      }
      cardOrdersInFlight.current.add(slot);
      setStatus('syncing');
      try {
        const response = await activateCard({ sessionId: id, slot, commandId: newCommandId() });
        /*
         * A resposta pertence à corrida em que o jogador ainda está?
         *
         * Se não pertence, ela é DESCARTADA inteira - nem estado, nem
         * instantânea, nem recusa. Adoptar a instantânea de uma sessão
         * substituída rebobinaria a arena, e mostrar a recusa dela seria
         * explicar ao jogador uma ordem que já não é sobre nada.
         */
        if (sessionId.current !== id) {
          return null;
        }
        /*
         * Uma liquidação aconteceu dentro da ordem, portanto um checkpoint em
         * voo pertence a um estado que já andou - a mesma invalidação que a
         * detonação faz.
         */
        invalidateInFlight();
        sessionId.current = response.session.sessionId;
        nextSequence.current = response.session.lastSequence + 1;
        windowStartTickMs.current = response.session.acceptedTickMs;
        setProgression(response.progression);
        bridge.sendToGame('hunt:card-activated', {
          snapshot: parseHuntSnapshot(response.snapshot),
          slot,
          accepted: response.accepted,
        });
        markSaved();
        return response.accepted ? null : response.refusal;
      } catch (error) {
        /*
         * Nada ligou. O estado da carta vive no servidor, portanto não há nada
         * para desfazer aqui - o jogador pode voltar a carregar.
         */
        setStatus(
          error instanceof HuntApiError && error.isUnauthorized ? 'unauthorized' : 'reconnecting',
        );
        return null;
      } finally {
        cardOrdersInFlight.current.delete(slot);
      }
    },
    [bridge, invalidateInFlight, markSaved],
  );

  /**
   * **Solta a carga de um espaço** (gate V28H.N).
   *
   * ## Porque é o gémeo de `activateCardSlot`, linha por linha
   *
   * Porque é o mesmo acto autoritativo com outro verbo: a mesma sessão, a mesma
   * regra de descarte para uma resposta atrasada, a mesma adopção da instantânea
   * e a mesma invalidação do checkpoint em voo. Escrever uma versão mais curta
   * seria escrever uma versão com menos defesas.
   *
   * ## Uma soltura que não encontra carga não é um erro
   *
   * `no_charge` chega quando a carga se completou sozinha entre o dedo do
   * jogador e a chegada do pedido - e nesse caso o feixe saiu, cheio. A barra
   * lê-a como as outras recusas: a instantânea que vem com ela diz a verdade.
   */
  const releaseCardSlot = useCallback(
    async (slot: CardSlot): Promise<CardOrderRefusal | null> => {
      const id = sessionId.current;
      if (!economic.current || id === null || halted.current) {
        return 'no_session';
      }
      if (cardReleasesInFlight.current.has(slot)) {
        return null;
      }
      cardReleasesInFlight.current.add(slot);
      setStatus('syncing');
      try {
        const response = await releaseCard({ sessionId: id, slot, commandId: newCommandId() });
        if (sessionId.current !== id) {
          return null;
        }
        invalidateInFlight();
        sessionId.current = response.session.sessionId;
        nextSequence.current = response.session.lastSequence + 1;
        windowStartTickMs.current = response.session.acceptedTickMs;
        setProgression(response.progression);
        bridge.sendToGame('hunt:card-activated', {
          snapshot: parseHuntSnapshot(response.snapshot),
          slot,
          accepted: response.accepted,
        });
        markSaved();
        return response.accepted ? null : response.refusal;
      } catch (error) {
        setStatus(
          error instanceof HuntApiError && error.isUnauthorized ? 'unauthorized' : 'reconnecting',
        );
        return null;
      } finally {
        cardReleasesInFlight.current.delete(slot);
      }
    },
    [bridge, invalidateInFlight, markSaved],
  );

  const equip = useCallback(
    async (itemInstanceId: string, expectedLoadoutVersion: number) => {
      setStatus('syncing');
      try {
        const response = await equipItem({
          itemInstanceId,
          expectedLoadoutVersion,
          // A fresh key per equip: this is a distinct intent, not a retry of
          // the previous one. The button is disabled while it is in flight, so
          // a double click cannot produce two.
          idempotencyKey: newIdempotencyKey('equip'),
        });
        // The settlement inside the equip consumed a sequence, so a checkpoint
        // still travelling belongs to a state that has moved on.
        invalidateInFlight();
        sessionId.current = response.session.sessionId;
        nextSequence.current = response.session.lastSequence + 1;
        windowStartTickMs.current = response.session.acceptedTickMs;
        setProgression(response.progression);
        setLoadout(response.loadout);
        bridge.sendToGame('hunt:reconfigure', {
          // Authoritative only in an economic session - see `hunt:reconfigure`
          // in `bridge.ts`. The swap itself always travels; what does not is a
          // snapshot from a row the server never settled.
          snapshot: economic.current ? parseHuntSnapshot(response.snapshot) : null,
          loadout: response.loadout,
        });
        markSaved();
      } catch (error) {
        setStatus(error instanceof HuntApiError && error.isUnauthorized ? 'unauthorized' : 'ready');
        // Rethrown so the panel can tell a conflict (refresh the list) from a
        // transport failure (say so and keep the old loadout).
        throw error;
      }
    },
    [bridge, invalidateInFlight, markSaved],
  );

  /**
   * Adopts the run a CONFIRMED skin change reconfigured (gate V23.2A.1).
   *
   * ## Why this takes an answer instead of making a request
   *
   * The wardrobe owns the HTTP call: it holds the `loadoutVersion` two tabs
   * fight over, the idempotency key of the intention in flight and the shelf
   * that has to be replaced by whatever the server says. Duplicating that here
   * would give the slot two owners. So the wardrobe sends the request and this
   * adopts the authoritative half of the answer - which is also why nothing in
   * this function can fail: it runs only after a 200 that the contract's schema
   * already validated.
   *
   * ## And why there is no second bootstrap
   *
   * `POST /api/skins/equip` already settled the tail, moved the slot and
   * rewrote the SAME session's frozen loadout, in one transaction. Asking
   * `/api/hunt/bootstrap` afterwards would add a round trip, a second
   * reconciliation and a second chance to disagree, and it would add no
   * authority at all - the answer being adopted here IS the authority.
   *
   * ## Null is the ordinary case
   *
   * The account was not hunting. The slot moved, the wardrobe redrew itself,
   * and there is no arena to tell. Changing a skin in a menu must never create
   * a session, so this returns without touching anything.
   */
  const adoptSkinChange = useCallback(
    (reconfiguration: SkinHuntReconfiguration | null) => {
      if (!reconfiguration) {
        return;
      }
      // The settlement inside the slot move consumed a sequence, so a
      // checkpoint still travelling belongs to a state that has moved on.
      invalidateInFlight();
      /*
       * The SAME session, and it is written rather than asserted.
       *
       * The server cannot answer with a different one - `changeSkinSlot` locks
       * the active row and reconfigures it in place - so this is the same id
       * that was already here. What genuinely moved is the sequence and the
       * accepted tick, and those two are the reason this assignment exists.
       */
      sessionId.current = reconfiguration.session.sessionId;
      nextSequence.current = reconfiguration.session.lastSequence + 1;
      windowStartTickMs.current = reconfiguration.session.acceptedTickMs;
      setProgression(reconfiguration.progression);
      setLoadout(reconfiguration.loadout);
      bridge.sendToGame('hunt:reconfigure', {
        /*
         * Authoritative only in an economic session - see `hunt:reconfigure`
         * in `bridge.ts`, and the same rule equipping and merging already
         * follow. A debug session is settled by nothing, so the snapshot on
         * its row still describes the instant it was created; adopting it
         * would take the arena back there. The loadout always travels, because
         * the vault decided it either way.
         */
        snapshot: economic.current ? parseHuntSnapshot(reconfiguration.snapshot) : null,
        loadout: reconfiguration.loadout,
      });
      markSaved();
    },
    [bridge, invalidateInFlight, markSaved],
  );

  /**
   * Merges two identical copies and keeps the run going.
   *
   * Nothing is applied optimistically, for the same reason equipping is not:
   * the vault decides which copies were spent and what replaced them. On
   * failure the previous copies simply stay, the arena keeps playing, and the
   * caller shows a short message.
   *
   * The answer reconfigures the SAME session in place - the wave, the boss and
   * the shots in flight all continue - and the artifact state is re-read from
   * the server rather than patched here.
   */
  const combine = useCallback(
    async (first: string, second: string) => {
      setStatus('syncing');
      try {
        const response = await combineArtifacts({
          firstInstanceId: first,
          secondInstanceId: second,
          // A fresh key per merge: a distinct intent, not a retry. The button
          // disables itself while it is in flight, so a double click cannot
          // produce two - and if one somehow arrived, the server would replay.
          idempotencyKey: newIdempotencyKey('combine'),
        });
        // The settlement inside the merge consumed a sequence, so a checkpoint
        // still travelling belongs to a state that has moved on.
        invalidateInFlight();
        sessionId.current = response.session.sessionId;
        nextSequence.current = response.session.lastSequence + 1;
        windowStartTickMs.current = response.session.acceptedTickMs;
        setProgression(response.progression);
        setArtifacts(response.artifacts);
        bridge.sendToGame('hunt:reconfigure', {
          // Same rule as equipping: the new totals always travel, the frozen
          // snapshot of a non-economic session never does.
          snapshot: economic.current ? parseHuntSnapshot(response.snapshot) : null,
          artifactEffects: effectsFromWire(response.artifacts.totals),
        });
        markSaved();
      } catch (error) {
        setStatus(error instanceof HuntApiError && error.isUnauthorized ? 'unauthorized' : 'ready');
        // Rethrown so the panel can tell a conflict (refresh the list) from a
        // transport failure (say so and keep the copies).
        throw error;
      }
    },
    [bridge, invalidateInFlight, markSaved],
  );

  /**
   * Activates a pet, or deactivates the active one.
   *
   * Nothing is applied optimistically, for the same reason equipping is not:
   * the vault decides which pet is active. The answer reconfigures the SAME
   * session in place - the wave, the boss and the bullets in the air all
   * continue - and the pet list is re-read rather than patched here.
   */
  const setActivePetAction = useCallback(
    async (petInstanceId: string | null) => {
      setStatus('syncing');
      try {
        const response = await activatePet({
          petInstanceId,
          expectedLoadoutVersion: pets?.loadoutVersion ?? 0,
          idempotencyKey: newIdempotencyKey('pet'),
        });
        // The settlement inside the change consumed a sequence, so a
        // checkpoint still travelling belongs to a state that has moved on.
        invalidateInFlight();
        sessionId.current = response.session.sessionId;
        nextSequence.current = response.session.lastSequence + 1;
        windowStartTickMs.current = response.session.acceptedTickMs;
        setProgression(response.progression);
        setPets(response.pets);
        setActivePetState(response.activePet);
        bridge.sendToGame('hunt:pet', {
          activePet: response.activePet,
          // Adopted only when it is authoritative - see `hunt:pet` in
          // `bridge.ts`. A non-economic session's stored snapshot never
          // advances, so restoring from it would rewind the run on screen.
          snapshot: economic.current ? parseHuntSnapshot(response.snapshot) : null,
        });
        markSaved();
      } catch (error) {
        setStatus(error instanceof HuntApiError && error.isUnauthorized ? 'unauthorized' : 'ready');
        // Rethrown so the panel can tell a conflict (refresh) from a transport
        // failure (say so and keep the current pet).
        throw error;
      }
    },
    [bridge, invalidateInFlight, markSaved, pets],
  );

  /**
   * Evolves two copies of the same pet.
   *
   * If the pet that evolves was active, the new form stays active - the server
   * handed the slot over inside the same transaction, so there is no moment
   * without a pet and nothing for the running hunt to notice beyond stronger
   * guns.
   */
  const evolvePetAction = useCallback(
    async (first: string, second: string) => {
      setStatus('syncing');
      try {
        const response = await evolvePet({
          firstInstanceId: first,
          secondInstanceId: second,
          idempotencyKey: newIdempotencyKey('pet-evolve'),
        });
        invalidateInFlight();
        sessionId.current = response.session.sessionId;
        nextSequence.current = response.session.lastSequence + 1;
        windowStartTickMs.current = response.session.acceptedTickMs;
        setProgression(response.progression);
        setPets(response.pets);
        setActivePetState(response.activePet);
        bridge.sendToGame('hunt:pet', {
          activePet: response.activePet,
          // Same rule as activating: authoritative only in an economic
          // session. This is the case that was measured - six seconds of
          // simulated time went backwards on every evolution in a debug run.
          snapshot: economic.current ? parseHuntSnapshot(response.snapshot) : null,
        });
        markSaved();
      } catch (error) {
        setStatus(error instanceof HuntApiError && error.isUnauthorized ? 'unauthorized' : 'ready');
        throw error;
      }
    },
    [bridge, invalidateInFlight, markSaved],
  );

  const restart = useCallback(async () => {
    const id = sessionId.current;
    // Nothing is restarted behind an expedition: the run on screen is the one
    // the entry closed, and the server would refuse to open another (V28I.BZ).
    if (!id || halted.current || suspension.current !== null) {
      return;
    }
    setStatus('syncing');
    /*
     * And the same rule from the other side (gate V27A.1).
     *
     * A press is the newest intent when it is made, and it can still be
     * overtaken: a bootstrap that lands while this is in flight adopts a
     * session, and this answer - for the run the player was leaving - would
     * then replace it. Symmetric to `runBootstrap`, and by the same ticket: the
     * client shows the session it adopted last, so the answer that wins must be
     * the one whose request was made last - which, for a press, it is.
     */
    const ticket = ++issued.current;
    restartsInFlight.current += 1;
    try {
      const response = await restartHunt({
        sessionId: id,
        idempotencyKey: newIdempotencyKey('restart'),
      });
      if (ticket < lastAdopted.current) {
        return;
      }
      lastAdopted.current = ticket;
      const message = applyBootstrap(response);
      bridge.sendToGame('hunt:session', message);
    } catch (error) {
      if (ticket < lastAdopted.current) {
        return;
      }
      setStatus(
        error instanceof HuntApiError && error.isUnauthorized ? 'unauthorized' : 'reconnecting',
      );
    } finally {
      /*
       * Always, and after the adoption above rather than before it.
       *
       * A counter that is not given back would leave the client refusing every
       * read for the rest of the session - which is a worse failure than the one
       * this guards against, and a silent one. It is decremented in `finally` so
       * a refusal, a network error and a success all release it.
       */
      restartsInFlight.current -= 1;
    }
  }, [applyBootstrap, bridge]);

  /**
   * The one place a mining answer is adopted.
   *
   * Nothing is applied optimistically anywhere in this feature: the panel and
   * the scene are drawn from whatever the server last said, so a refusal
   * leaves both exactly as they were.
   */
  const adoptMining = useCallback(
    (next: MiningState) => {
      setMining(next);
      // The scene redraws the rings from the authoritative fleet. It is told
      // over the bridge rather than reading state, so React never owns it.
      bridge.sendToGame('mining:state', {
        ships: next.ships,
        totalPower: next.totalPower,
        /*
         * Presentation fields only.
         *
         * The scene draws a smaller sphere because the SERVER sent a smaller
         * number, never the other way round: the health, the tier and the
         * scale reach the scene so it can draw them, and there is no path from
         * the scene back to anything that decides any of them.
         */
        planet: next.planet
          ? {
              tier: next.planet.tier,
              remainingHealth: next.planet.remainingHealth,
              maxHealth: next.planet.maxHealth,
              visualScaleBps: next.planet.visualScaleBps,
              maxStageFractionBps: next.planet.maxStageFractionBps,
              palette: next.planet.palette,
            }
          : null,
      });
    },
    [bridge],
  );

  /** Reads the mining screen. Called when the tab opens, never on a timer. */
  const loadMining = useCallback(async () => {
    setMiningLoading(true);
    setMiningError(null);
    try {
      adoptMining(await fetchMiningState());
    } catch (error) {
      if (error instanceof HuntApiError && error.isUnauthorized) {
        setStatus('unauthorized');
      }
      setMiningError(miningRefusal(error));
    } finally {
      setMiningLoading(false);
    }
  }, [adoptMining]);

  /**
   * Runs one mining action, adopting only what the server answers.
   *
   * Shared by all four so the ordering cannot drift between them: one in
   * flight at a time, the previous state untouched on a refusal, and the
   * server's own reason shown rather than a cheerful guess.
   */
  const runMiningAction = useCallback(
    async (action: () => Promise<MiningState>, notice: string | null) => {
      if (miningInFlight.current) {
        return;
      }
      miningInFlight.current = true;
      setMiningBusy(true);
      setMiningError(null);
      setMiningNotice(null);
      try {
        adoptMining(await action());
        if (notice !== null) {
          setMiningNotice(notice);
        }
      } catch (error) {
        if (error instanceof HuntApiError && error.isUnauthorized) {
          setStatus('unauthorized');
        }
        // Nothing was applied, so the panel and the ring still describe the
        // state the account is actually in.
        setMiningError(miningRefusal(error));
      } finally {
        miningInFlight.current = false;
        setMiningBusy(false);
      }
    },
    [adoptMining],
  );

  const assignMiningAction = useCallback(
    async (instanceId: string) => {
      await runMiningAction(() => assignMiningShip(instanceId), null);
    },
    [runMiningAction],
  );

  const removeMiningAction = useCallback(
    async (instanceId: string) => {
      await runMiningAction(() => removeMiningShip(instanceId), null);
    },
    [runMiningAction],
  );

  const collectMiningAction = useCallback(async () => {
    if (miningInFlight.current) {
      return;
    }
    // One key per INTENTION. Reused while this click is being retried; a new
    // click mints another, because collecting again is a different thing.
    const key = miningCollectKey.current ?? newIdempotencyKey('mine');
    miningCollectKey.current = key;
    miningInFlight.current = true;
    setMiningBusy(true);
    setMiningError(null);
    setMiningNotice(null);
    try {
      const answer = await collectMining(key);
      adoptMining(answer.state);
      setMiningNotice(`Coletado: ${formatOre(answer.collectedMicro)}`);
      miningCollectKey.current = null;
    } catch (error) {
      if (error instanceof HuntApiError && error.isUnauthorized) {
        setStatus('unauthorized');
      }
      setMiningError(miningRefusal(error));
      if (error instanceof HuntApiError && error.reason === 'nothing_to_collect') {
        // Nothing was there. The next attempt is a fresh intention rather than
        // a retry of one that could never have succeeded.
        miningCollectKey.current = null;
      }
    } finally {
      miningInFlight.current = false;
      setMiningBusy(false);
    }
  }, [adoptMining]);

  const cancelMiningAction = useCallback(async () => {
    await runMiningAction(() => cancelMining(), 'Mineração cancelada. O minério foi preservado.');
  }, [runMiningAction]);

  /**
   * Reads the map screen's contents from the server.
   *
   * Called when the screen opens and again after a journey, never on a timer:
   * unlocks change only when a boss dies, and that arrives inside a checkpoint
   * answer this hook already handles.
   *
   * A failed read leaves whatever was on screen. It describes maps the server
   * already knows about, and a stale copy beats an empty one.
   */
  const loadMaps = useCallback(async () => {
    setMapsLoading(true);
    try {
      setMaps(await fetchMapList());
    } catch (error) {
      if (error instanceof HuntApiError && error.isUnauthorized) {
        setStatus('unauthorized');
      }
    } finally {
      setMapsLoading(false);
    }
  }, []);

  /**
   * Travels to another unlocked map.
   *
   * ## Nothing is applied until the server has spoken
   *
   * There is no optimistic update anywhere here. The map indicator, the arena,
   * the mob scale, the boss encounter and the digest all come from the
   * authoritative answer - which is the same shape a restart returns, so
   * adopting it is the code that already exists - and a refusal leaves every
   * one of them exactly as it was.
   *
   * ## One journey at a time
   *
   * The ref is checked and set in the same tick, so a double click sends one
   * request. That is belt and braces: the server locks the progression row and
   * would answer the loser with the state the account is already in.
   *
   * ## No bootstrap, no reload
   *
   * The scene is handed the new session over the bridge and reconfigures in
   * place. The canvas is not recreated and the page is not reloaded.
   */
  const travel = useCallback(
    async (mapIndex: number) => {
      // No journey behind an expedition: the ship is not on any hunt map now.
      if (travelInFlight.current || halted.current || suspension.current !== null) {
        return;
      }
      travelInFlight.current = true;
      setTravelling(true);
      setTravelError(null);
      try {
        const response = await travelToMap({
          // The destination and a replay key. Nothing else: no account, no
          // current map, no unlock list, no session, no snapshot. The server
          // knows all of them and would not believe this side about any of it.
          mapIndex,
          idempotencyKey: newIdempotencyKey('travel'),
        });
        const message = applyBootstrap(response);
        bridge.sendToGame('hunt:session', message);
        // The screen the player is looking at, re-read from the server rather
        // than patched here: `current` moved, and so may `bossDefeated`.
        setMaps(await fetchMapList().catch(() => null));
      } catch (error) {
        if (error instanceof HuntApiError && error.isUnauthorized) {
          setStatus('unauthorized');
        }
        // The server's own REASON, put into words a player can read. The
        // map, the session, the XP, the Gold and the vault are all untouched,
        // because nothing was applied.
        setTravelError(travelRefusal(error));
      } finally {
        travelInFlight.current = false;
        setTravelling(false);
      }
    },
    [applyBootstrap, bridge],
  );

  /**
   * Feeding, unlocking and repainting.
   *
   * All three answer with the whole pet list and the active pet, and the scene
   * is told about the new state so the renderer picks it up WITHOUT restarting
   * anything. The hunt is untouched: mobs, boss, countdown and shots in flight
   * all carry on, because none of these operations creates a session.
   */
  const feedPetAction = useCallback(
    async (petInstanceId: string, foodDefinitionId: string) => {
      const response = await feedPet({
        petInstanceId,
        foodDefinitionId,
        idempotencyKey: newIdempotencyKey('pet-feed'),
      });
      setPets(response.pets);
      setActivePetState(response.activePet);
      bridge.sendToGame('hunt:pet-appearance', { activePet: response.activePet });
    },
    [bridge],
  );

  const unlockPetAppearanceAction = useCallback(async (unlockDefinitionId: string) => {
    // Nothing about the RUN changes: an unlock is an account capability, and
    // the pet in the arena is unaffected until an appearance is applied.
    //
    // The response is RETURNED, not discarded. It carries the whole palette as
    // the server now sees it - which is what an earlier build threw away,
    // leaving a swatch reading "locked, 1 item" after the sphere had already
    // been spent.
    return unlockPetAppearance({
      unlockDefinitionId,
      idempotencyKey: newIdempotencyKey('pet-unlock'),
    });
  }, []);

  const applyPetAppearanceAction = useCallback(
    async (petInstanceId: string, energyId: string, finishId: string) => {
      const response = await applyPetAppearance({
        petInstanceId,
        energyId,
        finishId,
        idempotencyKey: newIdempotencyKey('pet-appearance'),
      });
      setPets(response.pets);
      setActivePetState(response.activePet);
      // Presentation only: the renderer swaps materials in place. Nothing
      // about the simulation, the session or the checkpoint moves.
      bridge.sendToGame('hunt:pet-appearance', { activePet: response.activePet });
    },
    [bridge],
  );

  return {
    status,
    session,
    sessionId: sessionId.current,
    economic: economic.current,
    progression,
    offlineReward,
    dismissOfflineReward: () => setOfflineReward(null),
    allocate,
    detonateBleeding,
    activateCardSlot,
    releaseCardSlot,
    equip,
    adoptSkinChange,
    loadout,
    artifacts,
    economy,
    /*
     * Re-reads the wallet and the VIP window from the server.
     *
     * Exposed so a panel that made money move - the store - can bring the HUD
     * up to date without a reload. It sets React state and nothing else: the
     * session, the wave, the boss countdown and the shots in flight are
     * untouched, which is what makes it safe to call while a hunt is running.
     */
    refreshEconomy,
    refreshProgression,
    combine,
    pets,
    activePet,
    setActivePet: setActivePetAction,
    evolvePet: evolvePetAction,
    feedPet: feedPetAction,
    unlockPetAppearance: unlockPetAppearanceAction,
    applyPetAppearance: applyPetAppearanceAction,
    drops,
    dismissDrop: (dropId: string) =>
      setDrops((current) => current.filter((drop) => drop.dropId !== dropId)),
    equipmentDrops,
    cardDrops,
    // Removing it from the queue is all this does: the next one in line takes
    // its place immediately, and the id stays in the shown set so a replay
    // cannot bring it back.
    dismissEquipmentDrop: (dropId: string) =>
      setEquipmentDrops((current) => current.filter((drop) => drop.dropId !== dropId)),
    dismissCardDrop: (dropId: string) =>
      setCardDrops((current) => current.filter((drop) => drop.dropId !== dropId)),
    restart,
    // The existing conflict path, named. See the note on the interface.
    rebootstrap: runBootstrap,
    suspended,
    suspendedFor,
    suspendForOpenWorld,
    resumeFromOpenWorld,
    maps,
    mapsLoading,
    travelling,
    travelError,
    loadMaps,
    travel,
    mining,
    miningLoading,
    miningBusy,
    miningError,
    miningNotice,
    loadMining,
    assignMining: assignMiningAction,
    removeMining: removeMiningAction,
    collectMining: collectMiningAction,
    cancelMining: cancelMiningAction,
  };
}
